// Lead-Seite wie in Close, nur ruhiger: Kopf (Name, Status, Eckdaten; rechts Anrufen ▾, E-Mail, SMS, Notiz,
// Follow-up ▾, Custom Activity ▾), links die Daten in Close-Reihenfolge (Über, Follow-ups, Opportunities, Kontakte,
// Felder), rechts die Eingabe-Karte (Notiz, Custom Activity, E-Mail, SMS; öffnet sich über den Kopf) und der
// Aktivitäts-Feed. Echtzeit aktualisiert Lead, Aufgaben und Feed.
// Tasten: n Notiz, e E-Mail, t SMS, a Custom Activity, f Follow-up, s Status, c anrufen, j/k nächster/vorheriger Lead,
// Esc zurück.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiFehler, leadsApi, type Aktivitaet, type Aufgabe, type Kontakt, type Lead as LeadTyp, type LeadDetail } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { useMeldung } from "../components/Meldungen";
import { useAnrufen } from "../components/Telefonnummer";
import { AnrufProtokoll } from "../components/lead/AnrufProtokoll";
import { Eingabeleiste, NOTIZ } from "../components/lead/Eingabeleiste";
import { Feed, useFeed, type FeedFilter } from "../components/lead/Feed";
import { EMAIL, KommunikationKarte, SMS } from "../components/lead/KommunikationKarte";
import { aufgabenSortieren } from "../components/Aufgaben";
import { LeadKopf } from "../components/lead/LeadKopf";
import { LinkeSpalte } from "../components/lead/LinkeSpalte";
import { TL } from "../components/lead/leadTexte";
import { entwurfLesen, entwurfSetzen, hatEntwurf } from "../daten/entwuerfe";
import { detailAusCache, detailMerken, vorladen } from "../daten/leadCache";
import { fokusSetzen, useNachbarn } from "../daten/liste";
import { schreibAuftragLesen, SMS_VERFASSEN, type SmsVerfassenDetail } from "../daten/schreiben";
import { useMeta } from "../daten/meta";
import { useSeitenBefehle, useSeitenKopf } from "../daten/seite";
import { useNeuVerbunden } from "../daten/verbindung";
import { zuletztMerken } from "../daten/zuletzt";
import { AnrufKarte, useTelefonie } from "../telefonie";
import { T } from "../texte";
import { useTasten } from "../werkzeuge/tasten";
import "../components/lead/leadTiefe.css";
import "../styles/lead.css";

/** Offenes Eingabefeld eines Leads wiederherstellen, aber nur, wenn darin schon etwas steht. */
function gemerktOffen(leadId: string): string | null {
  const was = entwurfLesen<string>(`${leadId}|offen`);
  return was && hatEntwurf(`${leadId}|${was}`) ? was : null;
}

export default function Lead() {
  const { leadId: roh = "" } = useParams();
  const leadId = decodeURIComponent(roh);
  const [params] = useSearchParams();
  const hervorheben = params.get("kontakt");
  const springeZu = params.get("aktivitaet");
  const navigate = useNavigate();
  const meta = useMeta();
  const meldung = useMeldung();
  const anrufen = useAnrufen();
  const [detail, setDetail] = useState<LeadDetail | null>(() => detailAusCache(leadId));
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedtNeu, setLaedtNeu] = useState(false);
  const [filter, setFilter] = useState<FeedFilter>({ art: "alle" });
  const [anrufProtokoll, setAnrufProtokoll] = useState(false);
  // Zusammengeführt (Hinweis mit Link zum Ziel, Runde 7B/7C); gelöscht geht zurück zur Liste
  const [zusammengefuehrt, setZusammengefuehrt] = useState<string | null>(null);
  const [zielName, setZielName] = useState<string | null>(null);
  const feed = useFeed(leadId, filter);
  // Läuft ein Anruf an diesem Lead, steht sein Notizfeld oben im Verlauf. Nach dem Auflegen ist der Anruf EINE Karte im
  // Verlauf (Anruf-Aktivität mit der Notiz darin): sobald der Server die Aktivität kennt, wird sie hier nachgeladen.
  const nacharbeitAktivitaet = useTelefonie().nacharbeit;
  const anrufAktivitaetId = nacharbeitAktivitaet?.leadId === leadId ? (nacharbeitAktivitaet.call.activity_id ?? null) : null;
  const feedAktualisieren = feed.aktualisieren;
  const feedKennt = feed.eintraege.some((a) => a.id === anrufAktivitaetId);
  useEffect(() => {
    if (anrufAktivitaetId && !feedKennt) feedAktualisieren(anrufAktivitaetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anrufAktivitaetId]);
  // Offenes Eingabefeld (Notiz oder Custom-Activity-Typ) je Lead gemerkt, damit ein angefangener Eintrag
  // nach dem Wechsel zu einem anderen Lead und zurück wieder offen ist (leere Felder öffnen nicht wieder).
  const [offen, setOffenRoh] = useState<string | null>(() => gemerktOffen(leadId));
  const setOffen = useCallback(
    (was: string | null) => {
      setOffenRoh(was);
      entwurfSetzen(`${leadId}|offen`, was ?? undefined);
    },
    [leadId],
  );
  const [caMenue, setCaMenue] = useState(false);
  // Vorbelegter Empfänger, wenn die Seite über ein Brief-Symbol (Liste, Kontaktkarte) geöffnet wurde. n erzwingt
  // einen frischen Komposer, auch wenn die E-Mail-Karte schon offen war.
  const [empfaenger, setEmpfaenger] = useState<{ an: string | null; n: number } | null>(null);
  // Kontakt, dessen SMS-Symbol angeklickt wurde: der SMS-Komposer wählt dessen Mobilnummer vor.
  const [smsKontakt, setSmsKontakt] = useState<{ id: string; n: number } | null>(null);
  const ort = useLocation();
  const generation = useRef(0);
  const nachbarn = useNachbarn(leadId);

  const listeTitel = nachbarn?.kontext.titel;
  useSeitenKopf([
    nachbarn ? { label: listeTitel ?? "Liste", to: nachbarn.kontext.pfad } : { label: "Leads", to: "/leads" },
    { label: detail?.lead.display_name ?? detail?.lead.name ?? "Lead" },
  ]);

  const setzen = useCallback(
    (d: LeadDetail) => {
      detailMerken(leadId, d);
      setDetail(d);
    },
    [leadId],
  );

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setFehler(null);
      try {
        const d = await leadsApi.detail(leadId);
        if (gen !== generation.current) return;
        setzen(d);
        setFehler(null);
      } catch (e) {
        if (gen !== generation.current) return;
        // Lokal (noch) nicht vorhanden: einmal aus Close nachholen (nur lesend)
        if (e instanceof ApiFehler && e.code === "nicht_gefunden" && e.message !== "Unbekannter API-Pfad.") {
          try {
            const d = await leadsApi.neuLaden(leadId);
            if (gen !== generation.current) return;
            setzen(d);
            setFehler(null);
            return;
          } catch (e2) {
            if (gen === generation.current) setFehler(e2);
            return;
          }
        }
        if (!still || !detailAusCache(leadId)) setFehler(e);
      }
    },
    [leadId, setzen],
  );

  useEffect(() => {
    setDetail(detailAusCache(leadId));
    setFehler(null);
    setFilter({ art: "alle" });
    setZusammengefuehrt(null);
    setOffenRoh(gemerktOffen(leadId));
    setCaMenue(false);
    void laden();
    fokusSetzen(leadId);
  }, [leadId, laden]);

  // Auftrag aus dem Router-Zustand (Brief-Symbol): E-Mail-Karte öffnen, danach den Zustand leeren, damit Neuladen
  // oder Zurück den Komposer nicht erneut aufreißt. Steht hinter dem Zurücksetzen beim Lead-Wechsel, damit es gewinnt.
  useEffect(() => {
    const auftrag = schreibAuftragLesen(ort.state);
    if (!auftrag) return;
    setEmpfaenger({ an: auftrag.an, n: Date.now() });
    setOffen(EMAIL);
    navigate(`${ort.pathname}${ort.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ort.key]);

  // SMS-Symbol einer Kontaktkarte: Komposer mit diesem Kontakt als Empfänger öffnen
  useEffect(() => {
    const abnehmen = (e: Event) => {
      const d = (e as CustomEvent<SmsVerfassenDetail>).detail;
      if (!d || d.leadId !== leadId) return;
      e.preventDefault();
      setSmsKontakt({ id: d.contactId, n: Date.now() });
      setOffen(SMS);
    };
    window.addEventListener(SMS_VERFASSEN, abnehmen);
    return () => window.removeEventListener(SMS_VERFASSEN, abnehmen);
  }, [leadId, setOffen]);
  useEffect(() => {
    if (offen !== SMS) setSmsKontakt(null); // der Kopf-Knopf und die Taste t wählen wieder selbst
  }, [offen]);
  useEffect(() => setSmsKontakt(null), [leadId]);

  // Zuletzt geöffnet merken, Nachbarn vorladen
  const zuletztMarke = useRef("");
  useEffect(() => {
    if (detail?.lead.id !== leadId) return;
    // nur beim Öffnen des Leads (oder bei anderem Kontakt in der Adresse), nicht bei jedem Neuladen der Daten: sonst
    // verlöre der Eintrag den Kontakt, den man inzwischen in der Karte angeklickt hat
    const marke = `${leadId}|${hervorheben ?? ""}`;
    if (zuletztMarke.current === marke) return;
    zuletztMarke.current = marke;
    // Kommt man über einen Kontakt (Suche, ?kontakt=…), steht dessen Name grau hinter dem Lead (S02, wie bei Close)
    const k = hervorheben ? detail.contacts.find((c) => c.id === hervorheben) : undefined;
    zuletztMerken(
      { id: leadId, name: detail.lead.display_name ?? detail.lead.name ?? "Lead", status_label: detail.lead.status_label, kontakt_id: k?.id ?? null, kontakt: k ? k.name || k.display_name || null : null },
      k?.id ?? null,
    );
  }, [detail, leadId, hervorheben]);
  useEffect(() => {
    if (nachbarn?.nachher) vorladen(nachbarn.nachher);
  }, [nachbarn?.nachher]);

  // --- Echtzeit --------------------------------------------------------------
  const neuLadenZeit = useRef<number | undefined>(undefined);
  const stillNeuLaden = useCallback(() => {
    window.clearTimeout(neuLadenZeit.current);
    neuLadenZeit.current = window.setTimeout(() => void laden(true), 250);
  }, [laden]);
  useEffect(() => () => window.clearTimeout(neuLadenZeit.current), []);

  useEchtzeit(["lead.updated", "task.created", "task.updated"], (e) => {
    if (e.lead_id === leadId) stillNeuLaden();
  });
  useEchtzeit(["activity.created", "activity.updated", "activity.deleted"], (e) => {
    if (e.lead_id !== leadId) return;
    const id = String((e.data as { activity_id?: string }).activity_id ?? "");
    if (!id) feed.neu();
    else if (e.type === "activity.deleted") feed.entfernen(id);
    else feed.aktualisieren(id);
    if ((e.data as { activity_type?: string }).activity_type === "LeadStatusChange") stillNeuLaden();
  });
  useNeuVerbunden(() => {
    void laden(true);
    feed.neu();
  });

  // Lead wurde zusammengeführt oder gelöscht (Dubletten und Datenschutz, Agent 7C): Hinweis bzw. zurück zur Liste
  const mergedInto = (detail as (LeadDetail & { merged_into?: string | null }) | null)?.merged_into ?? null;
  const ziel = zusammengefuehrt ?? mergedInto;
  useEffect(() => {
    setZielName(null);
    if (!ziel) return;
    let weg = false;
    void leadsApi
      .detail(ziel)
      .then((d) => !weg && setZielName(d.lead.display_name ?? d.lead.name ?? null))
      .catch(() => undefined);
    return () => {
      weg = true;
    };
  }, [ziel]);
  const betrifftMich = (e: { lead_id?: string | null; data?: unknown }) =>
    e.lead_id === leadId || Object.values((e.data ?? {}) as Record<string, unknown>).some((v) => v === leadId);
  useEchtzeit(["lead.merged"], (e) => {
    if (!betrifftMich(e)) return;
    const d = (e.data ?? {}) as Record<string, unknown>;
    const andere = Object.values(d).find((v): v is string => typeof v === "string" && v.startsWith("lead_") && v !== leadId);
    if (andere) setZusammengefuehrt(andere);
    else stillNeuLaden();
  });
  useEchtzeit(["lead.deleted"], (e) => {
    if (!betrifftMich(e)) return;
    meldung.zeige("ok", TL.zusammen.geloescht);
    navigate(nachbarn?.kontext.pfad ?? "/leads", { replace: true });
  });

  // --- Änderungen aus den Bausteinen ----------------------------------------
  const leadSetzen = useCallback(
    (l: LeadTyp) => {
      setDetail((d) => {
        if (!d) return d;
        const neu = { ...d, lead: l };
        detailMerken(leadId, neu);
        return neu;
      });
    },
    [leadId],
  );

  const kontaktSetzen = (k: Kontakt) =>
    setDetail((d) => {
      if (!d) return d;
      const vorhanden = d.contacts.some((x) => x.id === k.id);
      const neu = { ...d, contacts: vorhanden ? d.contacts.map((x) => (x.id === k.id ? k : x)) : [...d.contacts, k] };
      detailMerken(leadId, neu);
      return neu;
    });

  // Kontakte per Ziehen ordnen: sofort umsortieren, im Hintergrund speichern; bei Fehler zurück auf die alte Reihenfolge
  const kontakteOrdnen = (ids: string[]) => {
    if (!detail) return;
    const vorher = detail.contacts;
    const sortiert = ids.map((id) => vorher.find((k) => k.id === id)).filter((k): k is Kontakt => !!k);
    const neueListe = [...sortiert, ...vorher.filter((k) => !ids.includes(k.id))];
    const setzen = (liste: Kontakt[]) =>
      setDetail((d) => {
        if (!d) return d;
        const neu = { ...d, contacts: liste };
        detailMerken(leadId, neu);
        return neu;
      });
    setzen(neueListe);
    leadsApi.kontakteOrdnen(leadId, neueListe.map((k) => k.id)).catch((e) => {
      setzen(vorher);
      meldung.fehler(e, "Reihenfolge nicht gespeichert");
    });
  };

  const aufgabeSetzen = (a: Aufgabe) =>
    setDetail((d) => {
      if (!d) return d;
      const ohne = d.tasks.filter((x) => x.id !== a.id);
      const tasks = a.is_complete ? ohne : aufgabenSortieren([...ohne, a]);
      const neu = { ...d, tasks };
      detailMerken(leadId, neu);
      return neu;
    });

  const statusSetzen = async (statusId: string) => {
    if (!detail) return;
    const vorher = detail.lead;
    const s = meta.status(statusId);
    leadSetzen({ ...vorher, status_id: statusId, status_label: s?.label ?? vorher.status_label });
    try {
      const r = await leadsApi.statusSetzen(leadId, statusId);
      leadSetzen(r.lead);
      meldung.ok(`Status: ${s?.label ?? r.lead.status_label}`);
    } catch (e) {
      leadSetzen(vorher);
      meldung.fehler(e, "Status nicht geändert");
    }
  };

  const neuAusClose = async () => {
    setLaedtNeu(true);
    try {
      const d = await leadsApi.neuLaden(leadId);
      setzen(d);
      feed.neu();
      meldung.ok("Neu aus Close geladen.");
    } catch (e) {
      meldung.fehler(e, "Nicht neu geladen");
    } finally {
      setLaedtNeu(false);
    }
  };

  const aktivitaetNeu = (a: Aktivitaet) => {
    feed.einfuegen(a);
  };

  // --- Tastatur und Befehle ------------------------------------------------
  const gehe = (id: string | null | undefined) => {
    if (!id) return;
    fokusSetzen(id);
    navigate(`/lead/${encodeURIComponent(id)}`);
  };
  const ersteNummer = detail?.contacts.flatMap((k) => k.phones.map((p) => ({ k, p })))[0];
  const followUp = () => document.querySelector<HTMLButtonElement>("[data-follow-up]")?.click();
  const statusOeffnen = () => document.getElementById("lead-status")?.click();

  useTasten({
    j: () => gehe(nachbarn?.nachher),
    k: () => gehe(nachbarn?.vorher),
    Escape: () => (offen ? setOffen(null) : nachbarn && navigate(nachbarn.kontext.pfad)),
    n: () => setOffen(NOTIZ),
    e: () => setOffen(EMAIL),
    t: () => setOffen(SMS),
    a: () => setCaMenue(true),
    f: followUp,
    s: statusOeffnen,
    c: () => ersteNummer && void anrufen(ersteNummer.p.phone, leadId, ersteNummer.k.id, { leadName: detail?.lead.display_name ?? detail?.lead.name, kontaktName: ersteNummer.k.name }),
  });

  useSeitenBefehle([
    { id: "notiz", label: "Notiz schreiben", icon: "notiz", taste: "n", aktion: () => setOffen(NOTIZ) },
    { id: "email", label: "E-Mail schreiben", icon: "mail", taste: "e", aktion: () => setOffen(EMAIL) },
    { id: "sms", label: "SMS schreiben", icon: "sms", taste: "t", aktion: () => setOffen(SMS) },
    ...(meta.meta?.custom_activity_types ?? [])
      .filter((t) => !t.is_archived && !t.api_create_only)
      .sort((x, y) => x.name.localeCompare(y.name, "de", { numeric: true }))
      .map((t) => ({ id: `ca-${t.id}`, label: `Custom Activity: ${t.name}`, icon: "aktivitaet" as const, aktion: () => setOffen(t.id) })),
    { id: "followup", label: "Follow-up für morgen anlegen", icon: "plus", taste: "f", aktion: followUp },
    { id: "status", label: "Status ändern …", icon: "status", taste: "s", aktion: statusOeffnen },
    { id: "anruf", label: T.lead.anrufProtokollieren, icon: "telefon", aktion: () => setAnrufProtokoll(true) },
    { id: "neu", label: T.lead.neuLaden, icon: "neu-laden", aktion: () => void neuAusClose() },
    ...(nachbarn?.nachher ? [{ id: "naechster", label: T.lead.naechster, icon: "pfeil-rechts" as const, taste: "j", aktion: () => gehe(nachbarn.nachher) }] : []),
  ]);

  if (!detail) {
    return fehler ? (
      <div className="seite">
        <h1>Lead nicht verfügbar</h1>
        <FehlerBox fehler={fehler} onNochmal={() => void laden()} />
      </div>
    ) : (
      <Laden text="Lead wird geladen …" />
    );
  }

  const ungefiltert = filter.art === "alle" && !filter.q && !filter.userId && !filter.contactId && !filter.von && !filter.bis;
  const letzteAktivitaet = ungefiltert ? (feed.eintraege[0]?.activity_at ?? feed.eintraege[0]?.date_created ?? null) : null;

  return (
    <div className="ls">
      <LeadKopf
        detail={detail}
        onLead={leadSetzen}
        onStatus={(id) => void statusSetzen(id)}
        onAufgabe={aufgabeSetzen}
        onNeuLaden={neuAusClose}
        onAnrufProtokoll={() => setAnrufProtokoll(true)}
        laedtNeu={laedtNeu}
        offen={offen}
        onOeffnen={setOffen}
        caMenue={caMenue}
        setCaMenue={setCaMenue}
        letzteAktivitaet={letzteAktivitaet}
      />
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} kompakt /> : null}
      {ziel ? (
        <div className="hinweis hinweis--info ls-zusammen" role="status">
          <span>{TL.zusammen.mitLead(zielName ?? "einem anderen Lead")}.</span>
          <button type="button" className="textknopf" onClick={() => navigate(`/lead/${encodeURIComponent(ziel)}`)}>
            {TL.zusammen.zumZiel}
          </button>
        </div>
      ) : null}
      <div className="ls-koerper">
        <aside className="ls-links" aria-label="Lead-Daten">
          <LinkeSpalte detail={detail} hervorheben={hervorheben} onLead={leadSetzen} onKontakt={kontaktSetzen} onKontakteOrdnen={kontakteOrdnen} onAufgabe={aufgabeSetzen} />
        </aside>
        <section className="ls-rechts" aria-label="Eintragen und Aktivitäten">
          {offen === EMAIL || offen === SMS ? (
            <KommunikationKarte
              key={`${leadId}|${offen}|${offen === SMS ? smsKontakt?.n ?? 0 : empfaenger?.n ?? 0}`}
              art={offen}
              leadId={leadId}
              an={offen === EMAIL ? empfaenger?.an ?? null : null}
              kontakt={offen === SMS ? smsKontakt?.id ?? null : null}
              kontakte={detail.contacts}
              onClose={() => setOffen(null)}
              onGesendet={() => feed.neu()}
            />
          ) : offen ? (
            <Eingabeleiste leadId={leadId} kontakte={detail.contacts} offen={offen} onSchliessen={() => setOffen(null)} onAktivitaet={aktivitaetNeu} />
          ) : null}
          <AnrufKarte leadId={leadId} />
          <Feed
            leadId={leadId}
            feed={feed}
            filter={filter}
            setFilter={setFilter}
            kontakte={detail.contacts}
            springeZu={springeZu}
          />
        </section>
      </div>
      <AnrufProtokoll offen={anrufProtokoll} leadId={leadId} kontakte={detail.contacts} onSchliessen={() => setAnrufProtokoll(false)} onGespeichert={aktivitaetNeu} />
    </div>
  );
}
